import { useCallback, useMemo, useState } from 'react';

import i18n from '@/utils/i18n';
import {
  MerchantOrderStatusEnum,
  OpsOrderCarrier,
  type OpsOrderUpdateInput,
  type OpsOrderContainerUpdateInput,
  type OpsOrderUntrackedItemUpdateInput,
  type OpsOrderItemUpdateInput,
} from '@/graphql/generatedTypes';

import type {
  AddressFormState,
  FieldErrors,
  OpsOrderDetail,
  OrderFormState,
} from './types';
import { getOrderKind } from './orderFormPolicy';
import {
  isAddressFieldRequired,
  isAddressFieldSupported,
  normalizeName,
  rulesForCarrier,
  validateAddress,
} from './addressRules';
import { orderTypeAllowsStoreAssets } from '../../utils/storeAssetOrderTypes';
import {
  isBlank,
  isUntrackedOtherLine,
  orderTypeRequiresLines,
} from '../../utils/opsOrderLineRules';
import { useFormDirtyTracking } from '../../hooks/useFormDirtyTracking';
import { initialWarehouseSelection } from './warehouseRouting';

function buildCtDetailMap(order: OpsOrderDetail) {
  return new Map(order.container_types_used_detail.map((c) => [c.id, c]));
}

function initFormState(order: OpsOrderDetail): OrderFormState {
  const addr = order.delivery_address;
  const ctMap = buildCtDetailMap(order);
  const containerItems: OrderFormState['items'] = order.containers.map(
    (item) => ({
      id: item.id,
      kind: 'container',
      type_id: item.type.id,
      item_location_id: item.item_location_id,
      amount: item.amount,
      typeName: item.type.name ?? `#${item.type.id}`,
      imageUrl: item.type.image_url ?? null,
      hexCode: ctMap.get(item.type.id)?.color_hex_code ?? null,
      purchasePriceCents: item.type.purchase_price_cents ?? 0,
      disabled: item.disabled,
    }),
  );
  const untrackedItems: OrderFormState['items'] = order.untracked_items.map(
    (item) => ({
      id: item.id,
      kind: 'untracked',
      type_id: item.type.id,
      item_location_id: item.item_location_id,
      amount: item.amount,
      typeName: item.name ?? item.type.name ?? `#${item.type.id}`,
      imageUrl: null,
      hexCode: null,
      purchasePriceCents: 0,
      disabled: false,
    }),
  );
  const storeAssetItems: OrderFormState['items'] = order.items.map((item) => ({
    id: item.id,
    kind: 'store_asset',
    type_id: item.item_type.id,
    item_location_id: item.item_location_id,
    amount: item.amount,
    typeName: item.name ?? item.item_type.name ?? `#${item.item_type.id}`,
    imageUrl: item.item_type.image_url ?? null,
    hexCode: item.item_type.color_hex_code ?? null,
    purchasePriceCents: 0,
    disabled: false,
  }));
  return {
    items: [...containerItems, ...untrackedItems, ...storeAssetItems],
    packageWeights: [...order.package_weights],
    address: {
      name: addr?.name ?? '',
      company: addr?.company ?? '',
      phone_number: addr?.phone_number ?? '',
      email: addr?.email ?? '',
      streetname: addr?.streetname ?? '',
      additional_info: addr?.additional_info ?? '',
      zip: addr?.zip ?? '',
      city: addr?.city ?? '',
      country: addr?.country ?? '',
    },
    comment: order.comment ?? '',
    opsComment: order.ops_comment ?? '',
    status: order.status,
    carrier: order.carrier ?? null,
    carrierService: order.carrier_service ?? null,
    language: order.language ?? null,
    warehouseUuid: initialWarehouseSelection(order),
  };
}

function toSnapshot(form: OrderFormState): string {
  return JSON.stringify({
    ...form,
    address: {
      ...form.address,
      name: normalizeName(form.address.name),
    },
  });
}

export function useOrderForm(order: OpsOrderDetail) {
  const [errors, setErrors] = useState<FieldErrors>({});
  const {
    orderKind,
    isReadOnly,
    canEditAddress,
    canEditItems,
    canEditPackageWeights,
    canEditCarrier,
    canEditWarehouse,
    canEditStatus,
    canEditLanguage,
  } = getOrderKind(order.type, order.status);

  const clearErrors = useCallback(() => setErrors({}), []);
  const clearItemErrors = useCallback(
    () =>
      setErrors((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(
            ([key]) => key !== 'items' && !key.startsWith('item.'),
          ),
        ),
      ),
    [],
  );

  const { form, setForm, isDirty, resetToInitial } = useFormDirtyTracking({
    order,
    initFormState,
    toSnapshot,
    onReset: clearErrors,
  });

  const rules = useMemo(
    () => rulesForCarrier(order, form.carrier),
    [order, form.carrier],
  );

  const setAddressField = useCallback(
    (field: keyof AddressFormState, value: string) => {
      if (!canEditAddress) return;
      setForm((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: value },
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`address.${field}`];
        return next;
      });
    },
    [canEditAddress, setForm],
  );

  const setItemAmount = useCallback(
    (index: number, amount: number) => {
      if (!canEditItems) return;
      setForm((prev) => ({
        ...prev,
        items: prev.items.map((item, i) =>
          i === index ? { ...item, amount } : item,
        ),
      }));
      clearItemErrors();
    },
    [canEditItems, setForm, clearItemErrors],
  );

  const removeItems = useCallback(
    (ids: string[]) => {
      if (!canEditItems || ids.length === 0) return;
      const idSet = new Set(ids);
      setForm((prev) => ({
        ...prev,
        items: prev.items.filter((item) => !idSet.has(item.id)),
      }));
      clearItemErrors();
    },
    [canEditItems, setForm, clearItemErrors],
  );

  const addItem = useCallback(
    (
      typeId: number,
      typeName: string,
      imageUrl: string | null,
      hexCode: string | null,
      amount: number,
    ) => {
      if (!canEditItems) return;
      setForm((prev) => ({
        ...prev,
        items: [
          ...prev.items,
          {
            id: crypto.randomUUID(),
            kind: 'container',
            type_id: typeId,
            amount,
            typeName,
            imageUrl,
            hexCode,
            purchasePriceCents: 0,
            disabled: false,
          },
        ],
      }));
      clearItemErrors();
    },
    [canEditItems, setForm, clearItemErrors],
  );

  const addUntrackedItem = useCallback(
    (typeId: number, typeName: string) => {
      if (!canEditItems) return;
      setForm((prev) => ({
        ...prev,
        items: [
          ...prev.items,
          {
            id: crypto.randomUUID(),
            kind: 'untracked',
            type_id: typeId,
            amount: 0,
            typeName,
            imageUrl: null,
            hexCode: null,
            purchasePriceCents: 0,
            disabled: false,
          },
        ],
      }));
      clearItemErrors();
    },
    [canEditItems, setForm, clearItemErrors],
  );

  const addStoreAssetItem = useCallback(
    (
      itemTypeId: number,
      typeName: string,
      imageUrl: string | null,
      hexCode: string | null,
    ) => {
      if (!canEditItems) return;
      setForm((prev) => ({
        ...prev,
        items: [
          ...prev.items,
          {
            id: crypto.randomUUID(),
            kind: 'store_asset',
            type_id: itemTypeId,
            amount: 0,
            typeName,
            imageUrl,
            hexCode,
            purchasePriceCents: 0,
            disabled: false,
          },
        ],
      }));
      clearItemErrors();
    },
    [canEditItems, setForm, clearItemErrors],
  );

  const setPackageWeight = useCallback(
    (index: number, weight: number) => {
      if (!canEditPackageWeights) return;
      setForm((prev) => ({
        ...prev,
        packageWeights: prev.packageWeights.map((w, i) =>
          i === index ? weight : w,
        ),
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`packageWeight.${index}`];
        return next;
      });
    },
    [canEditPackageWeights, setForm],
  );

  const setComment = useCallback(
    (value: string) => {
      if (isReadOnly) return;
      setForm((prev) => ({ ...prev, comment: value }));
    },
    [isReadOnly, setForm],
  );

  const setOpsComment = useCallback(
    (value: string) => {
      setForm((prev) => ({ ...prev, opsComment: value }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.opsComment;
        return next;
      });
    },
    [setForm],
  );

  const setStatus = useCallback(
    (value: MerchantOrderStatusEnum) => {
      if (!canEditStatus) return;
      setForm((prev) => ({ ...prev, status: value }));
    },
    [canEditStatus, setForm],
  );

  const setCarrier = useCallback(
    (value: OpsOrderCarrier) => {
      if (!canEditCarrier) return;
      setForm((prev) => ({ ...prev, carrier: value }));
    },
    [canEditCarrier, setForm],
  );

  const setLanguage = useCallback(
    (value: string | null) => {
      if (!canEditLanguage) return;
      setForm((prev) => ({ ...prev, language: value }));
    },
    [canEditLanguage, setForm],
  );

  const setWarehouseUuid = useCallback(
    (value: string | null) => {
      if (!canEditWarehouse) return;
      setForm((prev) => ({ ...prev, warehouseUuid: value }));
    },
    [canEditWarehouse, setForm],
  );

  const isFieldSupported = useCallback(
    (field: keyof AddressFormState): boolean =>
      isAddressFieldSupported(rules, field),
    [rules],
  );

  const isFieldRequired = useCallback(
    (field: keyof AddressFormState): boolean =>
      isAddressFieldRequired(rules, field),
    [rules],
  );

  // An inbound order shows the sender's address and no package weights, so
  // neither is validated there; its lines are locked and travel untouched. An
  // order is put on hold precisely when something like its address is still
  // missing, so On hold saves the address unvalidated. colugo checks it again
  // when the order is approved.
  const validate = useCallback((): FieldErrors | null => {
    const newErrors: FieldErrors =
      orderKind === 'outbound' && form.status !== MerchantOrderStatusEnum.OnHold
        ? validateAddress(rules, form.address)
        : {};

    if (orderKind === 'outbound') {
      for (let i = 0; i < form.packageWeights.length; i++) {
        if (form.packageWeights[i] <= 0) {
          newErrors[`packageWeight.${i}`] = i18n.t('Common:validationRequired');
        }
      }
    }

    // A line at amount 0 is dropped on save (see buildUpdateInput), so only
    // the others count here.
    const lines = form.items.filter((item) => item.amount >= 1);
    if (
      canEditItems &&
      orderTypeRequiresLines(order.type) &&
      lines.length === 0
    ) {
      newErrors.items = i18n.t('Order:validationNoItems');
    }

    if (
      orderKind === 'outbound' &&
      lines.some(isUntrackedOtherLine) &&
      isBlank(form.opsComment)
    ) {
      newErrors.opsComment = i18n.t(
        'Order:validationOpsCommentRequiredForOther',
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? null : newErrors;
  }, [
    form.address,
    form.packageWeights,
    form.items,
    form.opsComment,
    form.status,
    rules,
    orderKind,
    canEditItems,
    order.type,
  ]);

  const buildUpdateInput = useCallback((): OpsOrderUpdateInput => {
    // Setting a line to 0 is how the form removes it, as the legacy dialog
    // always did; colugo refuses a line at 0 outright.
    const lines = form.items.filter((item) => item.amount >= 1);
    const containers: OpsOrderContainerUpdateInput[] = lines
      .filter((item) => item.kind === 'container')
      .map((item) => ({
        id: item.id,
        type_id: item.type_id,
        amount: item.amount,
      }));
    const untracked_items: OpsOrderUntrackedItemUpdateInput[] = lines
      .filter((item) => item.kind === 'untracked')
      .map((item) => ({
        id: item.id,
        type_id: item.type_id,
        amount: item.amount,
      }));

    // STORE_ASSET lines live in their own collection: they all carry TypeId 0,
    // so colugo keys them by item_type_id instead. Sent only on the order types
    // that may carry them — on any other type there is no store-asset UI, and
    // omitting the key makes colugo clone whatever rows are already there rather
    // than read an empty list as "delete them all".
    const items: OpsOrderItemUpdateInput[] | undefined =
      orderTypeAllowsStoreAssets(order.type)
        ? lines
            .filter((item) => item.kind === 'store_asset')
            .map((item) => ({
              id: item.id,
              item_type_id: item.type_id,
              amount: item.amount,
            }))
        : undefined;

    return {
      // Locked lines are left out so colugo carries the stored rows forward
      // instead of re-validating a re-sent copy of them.
      ...(canEditItems && {
        containers,
        untracked_items,
        ...(items != null && { items }),
      }),
      package_weights: form.packageWeights,
      comment: form.comment,
      ops_comment: form.opsComment,
      ...(form.status !== order.status && { status: form.status }),
      carrier: form.carrier,
      carrier_service: form.carrierService,
      language: form.language ?? undefined,
      // Sent only when changed; null switches the order back to automatic.
      ...(form.warehouseUuid !== initialWarehouseSelection(order) && {
        warehouse_uuid: form.warehouseUuid,
      }),
      delivery_address: {
        name: normalizeName(form.address.name),
        company: form.address.company,
        phone_number: form.address.phone_number,
        email: form.address.email,
        streetname: form.address.streetname,
        additional_info: form.address.additional_info,
        zip: form.address.zip,
        city: form.address.city,
        country: form.address.country,
      },
    };
  }, [form, order, canEditItems]);

  const needsChangeGuard = useMemo(() => {
    if (
      form.status === MerchantOrderStatusEnum.Canceled &&
      form.status !== order.status
    ) {
      return true;
    }
    const initialItems = initFormState(order).items;
    if (form.items.length !== initialItems.length) return true;
    return form.items.some(
      (item, i) => item.amount !== initialItems[i]?.amount,
    );
  }, [form.status, form.items, order]);

  return {
    form,
    errors,
    setAddressField,
    setItemAmount,
    removeItems,
    addItem,
    addUntrackedItem,
    addStoreAssetItem,
    setPackageWeight,
    setComment,
    setOpsComment,
    setStatus,
    setCarrier,
    setLanguage,
    setWarehouseUuid,
    isFieldSupported,
    isFieldRequired,
    isReadOnly,
    canEditAddress,
    canEditItems,
    canEditPackageWeights,
    canEditCarrier,
    canEditWarehouse,
    canEditStatus,
    canEditLanguage,
    orderKind,
    isDirty,
    needsChangeGuard,
    resetToInitial,
    validate,
    buildUpdateInput,
  };
}
